import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyItemAiLookupReview,
  dispatchItemAiLookupApplied,
  fetchItemAiLookupRun,
} from '@/data/itemAiLookup';
import {
  buildItemAiLookupApplyDecisions,
  buildItemAiLookupReviewModel,
  displayReviewValue,
  type ReviewChoice,
  type ReviewFieldChoice,
  type ReviewGroup,
} from './itemAiLookupReviewModel';
import './ItemAiLookupReviewModal.css';

type Props = {
  runId: string;
  onClose: () => void;
};

type GroupFilter = 'all' | ReviewGroup;

const GROUPS: Array<{ key: GroupFilter; label: string }> = [
  { key: 'all', label: 'All suggestions' },
  { key: 'details', label: 'Item details' },
  { key: 'facets', label: 'Facets' },
  { key: 'gear', label: 'Accessories & cases' },
  { key: 'media', label: 'Media & documents' },
];

const GROUP_HEADINGS: Record<ReviewGroup, { title: string; hint: string }> = {
  details: { title: 'Item details', hint: 'Identity, description, and physical specs' },
  facets: { title: 'Facets', hint: 'Searchable technical capabilities' },
  gear: { title: 'Accessories & cases', hint: 'Required catalog relationships' },
  media: { title: 'Media, documents & firmware', hint: 'References and maintained files' },
};

function draftValue(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map(displayReviewValue).join(', ');
  return String(value);
}

function choiceIsWide(choice: ReviewChoice): boolean {
  return choice.kind === 'image'
    || choice.kind === 'document'
    || (choice.kind === 'field' && choice.longText);
}

function documentUrl(choice: ReviewChoice): string | null {
  if (choice.kind !== 'document') return null;
  const url = choice.proposed.url;
  return typeof url === 'string' && url.trim() ? url.trim() : null;
}

function imageUrl(choice: ReviewChoice): string | null {
  if (choice.kind !== 'image') return null;
  const url = choice.proposed.url;
  return typeof url === 'string' && url.trim() ? url.trim() : null;
}

function ChoiceComparison({ choice }: { choice: Exclude<ReviewChoice, ReviewFieldChoice> }) {
  const href = documentUrl(choice);
  const image = imageUrl(choice);

  if (choice.kind === 'image' && image) {
    const imageNote = choice.proposed.notes || choice.proposed.source_url || 'Ready to import';
    return (
      <div className="item-ai-review__image">
        <img src={image} alt={choice.label} />
        <div className="stack gap-xxs">
          <span className="item-ai-review__value">
            <small>{choice.operation === 'Replace' ? 'Current source' : 'Suggested addition'}</small>
            {choice.operation === 'Replace' ? displayReviewValue(choice.current) : displayReviewValue(imageNote)}
          </span>
          <a className="item-ai-review__link" href={image} target="_blank" rel="noreferrer">Preview full image</a>
        </div>
      </div>
    );
  }

  if (choice.kind === 'document' && href) {
    return (
      <div className="stack gap-xxs">
        <span className="item-ai-review__value">
          <small>{choice.importAsManual ? 'Official manual' : 'Suggested document'}</small>
          {displayReviewValue(choice.proposed.kind)}
        </span>
        <a className="item-ai-review__link" href={href} target="_blank" rel="noreferrer">{href}</a>
      </div>
    );
  }

  return (
    <div className="item-ai-review__comparison">
      {choice.current ? (
        <>
          <span className="item-ai-review__value">
            <small>Current</small>
            {displayReviewValue(choice.current)}
          </span>
          <span className="item-ai-review__arrow" aria-hidden="true">→</span>
        </>
      ) : (
        <>
          <span className="item-ai-review__value">
            <small>Current</small>
            Not set
          </span>
          <span className="item-ai-review__arrow" aria-hidden="true">+</span>
        </>
      )}
      <span className="item-ai-review__value">
        <small>AI suggestion</small>
        {displayReviewValue(choice.proposed)}
      </span>
    </div>
  );
}

export default function ItemAiLookupReviewModal({ runId, onClose }: Props) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['item-ai-lookup-review', runId],
    queryFn: () => fetchItemAiLookupRun(runId),
    enabled: Boolean(runId),
  });
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());
  const [draftValues, setDraftValues] = React.useState<Record<string, string>>({});
  const [activeGroup, setActiveGroup] = React.useState<GroupFilter>('all');
  const [decisionMessage, setDecisionMessage] = React.useState<string | null>(null);

  const run = query.data?.run ?? null;
  const item = query.data?.item ?? null;
  const model = React.useMemo(
    () => (run ? buildItemAiLookupReviewModel(run, item) : { patch: {}, choices: [], unchangedCount: 0 }),
    [item, run],
  );
  const selectedCount = model.choices.filter((choice) => selectedIds.has(choice.id)).length;
  const visibleChoices = activeGroup === 'all'
    ? model.choices
    : model.choices.filter((choice) => choice.group === activeGroup);
  const itemLabel = item
    ? [item.item_code, item.name_internal].filter(Boolean).join(' · ') || item.item_id
    : 'Item';
  const itemHref = item?.item_id
    ? `/admin/items/editor?itemId=${encodeURIComponent(item.item_id)}&tab=basics`
    : '/admin/items';
  const host = document.getElementById('portal-root') || document.body;

  const groupCounts = React.useMemo(() => {
    const counts: Record<GroupFilter, number> = {
      all: model.choices.length,
      details: 0,
      facets: 0,
      gear: 0,
      media: 0,
    };
    model.choices.forEach((choice) => {
      counts[choice.group] += 1;
    });
    return counts;
  }, [model.choices]);

  const applyMutation = useMutation({
    mutationFn: () => {
      const decisions = buildItemAiLookupApplyDecisions({
        choices: model.choices,
        selectedIds,
        draftValues,
        item,
      });
      return applyItemAiLookupReview({
        runId,
        updates: decisions.updates,
        rejectedKeys: decisions.rejectedKeys,
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(['item-ai-lookup-review', runId], {
        run: result.run,
        item: result.item,
      });
      if (result.item?.item_id) {
        dispatchItemAiLookupApplied({
          itemId: result.item.item_id,
          runId,
          item: result.item,
          appliedKeys: result.appliedKeys,
        });
      }
      const warningText = result.warnings?.length
        ? ` ${result.warnings.map((warning) => warning.message).join(' ')}`
        : '';
      setDecisionMessage(`Applied ${selectedCount} selected suggestion${selectedCount === 1 ? '' : 's'} to the item.${warningText}`);
    },
  });

  React.useEffect(() => {
    const nextSelected = new Set<string>();
    const nextDrafts: Record<string, string> = {};
    model.choices.forEach((choice) => {
      nextSelected.add(choice.id);
      if (choice.kind === 'field') nextDrafts[choice.id] = draftValue(choice.proposed);
    });
    setSelectedIds(nextSelected);
    setDraftValues(nextDrafts);
  }, [model.choices, runId]);

  React.useEffect(() => {
    setDecisionMessage(null);
    setActiveGroup('all');
  }, [runId]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || applyMutation.isPending) return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [applyMutation.isPending, onClose]);

  const toggleChoice = React.useCallback((id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setVisibleSelected = React.useCallback((selected: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      visibleChoices.forEach((choice) => {
        if (selected) next.add(choice.id);
        else next.delete(choice.id);
      });
      return next;
    });
  }, [visibleChoices]);

  const updateDraft = React.useCallback((choiceId: string, value: string) => {
    setDraftValues((current) => ({ ...current, [choiceId]: value }));
  }, []);

  const applySelected = React.useCallback(() => {
    if (!model.choices.length || applyMutation.isPending) return;
    setDecisionMessage(null);
    void applyMutation.mutateAsync();
  }, [applyMutation, model.choices.length]);

  const renderChoice = (choice: ReviewChoice) => {
    const selected = selectedIds.has(choice.id);
    return (
      <article
        key={choice.id}
        className="item-ai-review__choice"
        data-selected={selected}
        data-wide={choiceIsWide(choice)}
        data-proposal-key={choice.proposalKey}
      >
        <label className="item-ai-review__check">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => toggleChoice(choice.id)}
            disabled={applyMutation.isPending}
            aria-label={`Apply ${choice.label}`}
          />
        </label>
        <div className="item-ai-review__choice-content">
          <div className="item-ai-review__choice-header">
            <span className="item-ai-review__choice-title">{choice.label}</span>
            <span className="item-ai-review__operation">{choice.operation}</span>
          </div>
          {choice.kind === 'field' ? (
            <div className="item-ai-review__comparison">
              <span className="item-ai-review__value">
                <small>Current</small>
                {displayReviewValue(choice.current)}
              </span>
              <span className="item-ai-review__arrow" aria-hidden="true">→</span>
              <span className="item-ai-review__value">
                <small>Apply this value</small>
                {choice.longText ? (
                  <textarea
                    className="item-ai-review__editor"
                    rows={3}
                    value={draftValues[choice.id] ?? ''}
                    onChange={(event) => updateDraft(choice.id, event.target.value)}
                    disabled={!selected || applyMutation.isPending}
                    aria-label={`${choice.label} proposed value`}
                  />
                ) : (
                  <input
                    className="item-ai-review__editor"
                    type={choice.inputType}
                    step={choice.inputType === 'number' ? 'any' : undefined}
                    value={draftValues[choice.id] ?? ''}
                    onChange={(event) => updateDraft(choice.id, event.target.value)}
                    disabled={!selected || applyMutation.isPending}
                    aria-label={`${choice.label} proposed value`}
                  />
                )}
              </span>
            </div>
          ) : (
            <ChoiceComparison choice={choice} />
          )}
        </div>
      </article>
    );
  };

  const groupsToRender = GROUPS
    .filter((group): group is { key: ReviewGroup; label: string } => group.key !== 'all')
    .filter((group) => activeGroup === 'all' || group.key === activeGroup)
    .filter((group) => visibleChoices.some((choice) => choice.group === group.key));

  return createPortal(
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="item-ai-review-title"
      onClick={() => {
        if (!applyMutation.isPending) onClose();
      }}
      style={{ zIndex: 14000 }}
    >
      <div className="modal item-ai-review" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header item-ai-review__header">
          <div className="item-ai-review__heading stack gap-xxs">
            <span className="item-ai-review__eyebrow">AI research review</span>
            <h2 id="item-ai-review-title">Choose what to add to this item</h2>
            <p className="item-ai-review__subtitle">
              {itemLabel}{run?.completed_at ? ` · Researched ${new Date(run.completed_at).toLocaleString()}` : ''}
            </p>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            disabled={applyMutation.isPending}
            aria-label="Cancel"
          >
            ×
          </button>
        </div>

        <div className="modal-body item-ai-review__body">
          {query.isLoading && <p className="fg-muted" style={{ textAlign: 'center', padding: '2rem 0' }}>Loading AI suggestions…</p>}
          {query.isError && (
            <div className="alert alert-error" role="alert" style={{ margin: '1.25rem' }}>
              {(query.error as Error)?.message || 'Failed to load AI item lookup results.'}
            </div>
          )}
          {!query.isLoading && !query.isError && run && (
            <>
              {run.error && <div className="alert alert-error" role="alert" style={{ margin: '1rem 1.35rem 0' }}>{run.error}</div>}
              {applyMutation.isError && (
                <div className="alert alert-error" role="alert" style={{ margin: '1rem 1.35rem 0' }}>
                  {(applyMutation.error as Error)?.message || 'Failed to apply the selected AI suggestions.'}
                </div>
              )}
              {decisionMessage && <div className="alert alert-success" role="status" style={{ margin: '1rem 1.35rem 0' }}>{decisionMessage}</div>}

              <div className="item-ai-review__overview">
                <div>
                  <div className="item-ai-review__overview-count">
                    <strong>{model.choices.length}</strong>
                    <span>new or changed suggestion{model.choices.length === 1 ? '' : 's'} ready to review</span>
                  </div>
                  <div className="item-ai-review__overview-note">
                    {model.unchangedCount > 0
                      ? `${model.unchangedCount} identical or duplicate result${model.unchangedCount === 1 ? '' : 's'} hidden automatically.`
                      : 'Only verified changes and additions are shown.'}
                  </div>
                </div>
                {model.choices.length > 0 && (
                  <div className="item-ai-review__bulk-actions">
                    <button type="button" className="btn subtle sm" onClick={() => setVisibleSelected(true)} disabled={applyMutation.isPending}>
                      Select visible
                    </button>
                    <button type="button" className="btn subtle sm" onClick={() => setVisibleSelected(false)} disabled={applyMutation.isPending}>
                      Clear visible
                    </button>
                  </div>
                )}
              </div>

              {model.choices.length > 0 && (
                <nav className="item-ai-review__filters" aria-label="Filter AI suggestions">
                  {GROUPS.filter((group) => group.key === 'all' || groupCounts[group.key] > 0).map((group) => (
                    <button
                      key={group.key}
                      type="button"
                      className="item-ai-review__filter"
                      data-active={activeGroup === group.key}
                      aria-pressed={activeGroup === group.key}
                      onClick={() => setActiveGroup(group.key)}
                    >
                      {group.label}
                      <span className="item-ai-review__filter-count">{groupCounts[group.key]}</span>
                    </button>
                  ))}
                </nav>
              )}

              <div className="item-ai-review__content">
                {model.choices.length === 0 ? (
                  <div className="item-ai-review__empty">
                    <strong>This item already matches the AI result</strong>
                    <p>No replacement or addition needs a decision. Identical suggestions were removed from the review.</p>
                  </div>
                ) : (
                  groupsToRender.map((group) => {
                    const groupChoices = visibleChoices.filter((choice) => choice.group === group.key);
                    return (
                      <section key={group.key} className="stack gap-sm" aria-labelledby={`item-ai-review-${group.key}`}>
                        <div className="item-ai-review__section-heading">
                          <h3 id={`item-ai-review-${group.key}`}>{GROUP_HEADINGS[group.key].title}</h3>
                          <span>{GROUP_HEADINGS[group.key].hint}</span>
                        </div>
                        <div className="item-ai-review__choice-grid">
                          {groupChoices.map(renderChoice)}
                        </div>
                      </section>
                    );
                  })
                )}
              </div>

              {Array.isArray(run.sources) && run.sources.length > 0 && (
                <details className="item-ai-review__sources">
                  <summary>Research evidence ({run.sources.length})</summary>
                  <ul>
                    {run.sources.slice(0, 16).map((source, index) => (
                      <li key={`${displayReviewValue(source)}-${index}`}>{displayReviewValue(source)}</li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>

        <div className="modal-actions item-ai-review__footer">
          <div className="item-ai-review__footer-summary" aria-live="polite">
            <strong>{selectedCount} of {model.choices.length} selected</strong>
            <span>Unchecked suggestions leave the item unchanged.</span>
          </div>
          <Link to={itemHref} className="btn subtle" onClick={onClose}>Open item editor</Link>
          <button
            type="button"
            className="btn btn-primary"
            onClick={applySelected}
            disabled={applyMutation.isPending || model.choices.length === 0}
            aria-label="Apply selected suggestions"
          >
            {applyMutation.isPending
              ? 'Applying…'
              : selectedCount > 0
                ? `Apply ${selectedCount} suggestion${selectedCount === 1 ? '' : 's'}`
                : 'Reject remaining suggestions'}
          </button>
        </div>
      </div>
    </div>,
    host,
  );
}
